import { useRef, useState } from 'react';
import createCacheStorage from './cache';

export default function createResourceManager() {
  const cache = createCacheStorage();
  const fetchPromises = {};
  const requests = {};

  let apiBaseUrl = '';
  let headers = {
    Accept: 'application/json'
  };

  function formatApiUrl(path) {
    return /^http/.test(path) ? path : `${apiBaseUrl}${path[0] !== '/' ? '/' + path : path}`;
  }

  function get(url, customHeaders = {}) {
    const requestUrl = formatApiUrl(url);
    const options = {
      headers: {
        ...headers,
        ...customHeaders
      },
      method: 'GET'
    };

    const key = `${requestUrl}/${JSON.stringify(options)}`;

    if (fetchPromises[key]) {
      return fetchPromises[key];
    }

    const promise = new Promise(async (resolve, reject) => {
      try {
        const response = await fetch(requestUrl, options);
        const body = await response.json();

        if (fetchPromises[key]) {
          delete fetchPromises[key];
        }

        resolve({
          ...response,
          body
        });
      } catch (error) {
        reject(error);
      }
    });

    fetchPromises[key] = promise;

    return promise;
  }

  return {
    setClientHeaders(customHeaders) {
      headers = {
        ...headers,
        ...customHeaders
      };
    },

    setApiBase(path) {
      apiBaseUrl = path;
    },

    useResource(name, type, url) {
      const key = `${name}.${type}.${url}`;

      const keyRef = useRef('');

      const [response, setResponse] = useState(cache.get(key));

      if (key in requests) {
        throw requests[key];
      }

      if (keyRef.current !== key) {
        keyRef.current = key;

        if (cache.has(key)) {
          setResponse(cache.get(key));
        } else if (!(key in requests)) {
          requests[key] = new Promise(resolve => {
            get(url)
              .then(res => {
                setResponse(res);
                cache.set(key, res);
                delete requests[key];
                resolve(res);
              })
              .catch(res => {
                setResponse(res);
                delete requests[key];
                resolve(res);
              });
          });

          throw requests[key];
        }
      }

      return response;
    },

    clearCache(name, type, url) {
      let key = name;

      if (type) {
        key = `${key}.${type}`;
      }

      if (url) {
        key = `${key}.${url}`;
      }

      cache.removeAll(key);
    }
  };
}
